export const Roles = {
  // UI display constants (uppercase — used in signup/display comparisons)
  ACHIEVER: "ACHIEVER",
  ADVISOR: "ADVISOR",

  // API role name constants (lowercase — matches what the backend returns)
  achiever: "achiever",
  advisor: "advisor",
  admin: "admin",
  super_admin: "super_admin",
  lead: "lead",
};

// Set of all role names that grant admin access
export const ADMIN_ROLES = new Set([Roles.admin, Roles.super_admin]);

// Program membership role constants (matches backend MembershipRoleEnum, which
// serializes the program-creator/lead role as "LEAD", not "LEAD_ADVISOR").
//
// LEAD is shared by every program type. The other four pair up: an Expert
// Program has CO_ADVISOR + ACHIEVER, a Peer Advisory group has FACILITATOR +
// PEER. They are separate roles rather than reused ones because the
// relationships differ -- a facilitator runs a group of peers who advise each
// other, which is not what a co-expert does for achievers.
export const MembershipRole = {
  LEAD_ADVISOR: "LEAD",
  CO_ADVISOR: "CO_ADVISOR",
  ACHIEVER: "ACHIEVER",
  FACILITATOR: "FACILITATOR",
  PEER: "PEER",
  // Organisation program team member. The founder is LEAD and co-runners are
  // FACILITATOR; per-item duties (driver, owner, contributor) are not roles.
  MEMBER: "MEMBER",
} as const;

export type MembershipRoleType = (typeof MembershipRole)[keyof typeof MembershipRole];

export const MembershipRoleLabel: Record<MembershipRoleType, string> = {
  [MembershipRole.LEAD_ADVISOR]: "Lead Expert",
  [MembershipRole.CO_ADVISOR]: "Co-Expert",
  [MembershipRole.ACHIEVER]: "Achiever",
  [MembershipRole.FACILITATOR]: "Peer Facilitator",
  [MembershipRole.PEER]: "Peer",
  [MembershipRole.MEMBER]: "Team Member",
};

/**
 * Organisation programs label the shared LEAD / FACILITATOR roles differently
 * ("Founder" rather than "Lead Expert"), per the Overview banner's "managed by
 * founders and facilitators". Same role values, different words.
 */
export const OrgMembershipRoleLabel: Partial<Record<MembershipRoleType, string>> = {
  [MembershipRole.LEAD_ADVISOR]: "Founder",
  [MembershipRole.FACILITATOR]: "Facilitator",
  [MembershipRole.MEMBER]: "Team Member",
};

/** Roles that RUN a program, for any type. */
export const PROGRAM_STAFF_ROLES: MembershipRoleType[] = [
  MembershipRole.LEAD_ADVISOR,
  MembershipRole.CO_ADVISOR,
  MembershipRole.FACILITATOR,
];

/** Roles that PARTICIPATE in a program, for any type. */
export const PROGRAM_PARTICIPANT_ROLES: MembershipRoleType[] = [
  MembershipRole.ACHIEVER,
  MembershipRole.PEER,
];
